import {
  type DependenciesValues,
  buildDataSource,
  isDependenciesFilled,
} from '@shared/helpers/DataSourceHelpers';
import { useMemo } from 'react';

/**
 * Custom hook that generates a data source string based on provided dependencies and their values.
 *
 * @param baseDataSource - The base data source string that may contain placeholders for dependencies.
 * @param dependencies - An optional array of dependency keys that need to be replaced in the base data source.
 * @param dependenciesValues - An optional object mapping dependency keys to their corresponding values.
 * @returns An object containing:
 *   - `dependenciesFilled`: A boolean indicating whether all dependencies have been provided with values.
 *   - `dataSource`: The final data source string with dependencies replaced by their values, if all dependencies are filled. Otherwise, the base data source string. When no dependencies are provided, the base data source string is returned.
 */
export const useDataSource = (
  baseDataSource: string,
  dependencies?: string[],
  dependenciesValues?: DependenciesValues
): {
  dependenciesFilled: boolean;
  dataSource: string;
} => {
  const dependenciesFilled = isDependenciesFilled(
    dependencies,
    dependenciesValues
  );

  const dataSource = useMemo(
    () =>
      buildDataSource(
        baseDataSource,
        dependencies,
        dependenciesValues,
        dependenciesFilled
      ),
    [dependenciesFilled, dependenciesValues, baseDataSource, dependencies]
  );

  return {
    dataSource,
    dependenciesFilled,
  };
};
